import { Helmet } from "react-helmet-async";
import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../Utils/Axios";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

function AdminRecoveryManagement() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [recoveries, setRecoveries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const getToken = () => localStorage.getItem("adminToken");

  useEffect(() => {
    const token = getToken();
    if (!token) {
      navigate("/admin/login");
      return;
    }
    fetchCompanies();
  }, [navigate]);

  const fetchCompanies = async () => {
    try {
      const token = getToken();
      const response = await api.get("/admin/companies", {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanies(response.data.companies || []);
    } catch (error) {
      console.error("Fetch companies error:", error);
      toast.error("Failed to load companies");
    }
  };

  const [showRecoveryModal, setShowRecoveryModal] = useState(false);
  const [editingRecovery, setEditingRecovery] = useState(null);
  const [recoveryFormData, setRecoveryFormData] = useState({
    serialNumber: '',
    customerPartyId: '',
    customerName: '',
    customerNameUrdu: '',
    customerVehicleNumber: '',
    customerPhone: '',
    recoveryAmount: '',
    paymentMethod: '',
    bankId: '',
    description: '',
    descriptionUrdu: '',
    date: new Date().toLocaleDateString('en-CA')
  });
  const [ledgerParties, setLedgerParties] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [showFieldModal, setShowFieldModal] = useState(false);

  // Recovery form fields
  const [recoveryFields, setRecoveryFields] = useState([
    { fieldName: 'Date', fieldNameUrdu: 'تاریخ', fieldType: 'date', isRequired: true, order: 0, isDefault: true },
    { fieldName: 'Customer Name', fieldNameUrdu: 'کسٹمر کا نام', fieldType: 'text', isRequired: true, order: 1, isDefault: true },
    { fieldName: 'Customer Name Urdu', fieldNameUrdu: 'کسٹمر کا نام اردو', fieldType: 'text', isRequired: false, order: 2, isDefault: true },
    { fieldName: 'Vehicle Number', fieldNameUrdu: 'گاڑی نمبر', fieldType: 'text', isRequired: false, order: 3, isDefault: true },
    { fieldName: 'Phone Number', fieldNameUrdu: 'فون نمبر', fieldType: 'text', isRequired: false, order: 4, isDefault: true },
    { fieldName: 'Recovery Amount', fieldNameUrdu: 'ریکوری کی رقم', fieldType: 'number', isRequired: true, order: 5, isDefault: true },
    { fieldName: 'Payment Method', fieldNameUrdu: 'ادائیگی کا طریقہ', fieldType: 'text', isRequired: false, order: 6, isDefault: true },
    { fieldName: 'Description', fieldNameUrdu: 'تفصیل', fieldType: 'textarea', isRequired: false, order: 7, isDefault: true },
    { fieldName: 'Description Urdu', fieldNameUrdu: 'تفصیل اردو', fieldType: 'textarea', isRequired: false, order: 8, isDefault: true }
  ]);

  // Memoize ReactQuill modules
  const quillModules = useMemo(() => ({
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'align': [] }],
      ['clean']
    ]
  }), []);

  const fetchLedgerParties = async () => {
    if (!selectedCompany) return;
    try {
      const token = getToken();
      // This would need admin endpoint to get ledger parties for a company
      setLedgerParties([]);
    } catch (error) {
      console.error("Fetch ledger parties error:", error);
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      const response = await api.get("/payment-method/active");
      setPaymentMethods(response.data.paymentMethods || []);
    } catch (error) {
      console.error("Fetch payment methods error:", error);
      setPaymentMethods([]);
    }
  };

  useEffect(() => {
    if (selectedCompany) {
      fetchRecoveries(selectedCompany._id);
      fetchLedgerParties();
      fetchPaymentMethods();
      loadRecoveryFields();
    }
  }, [selectedCompany]);

  const loadRecoveryFields = async () => {
    if (!selectedCompany) return;

    try {
      const token = getToken();
      // Try to load saved fields from backend or localStorage
      const storageKey = `recoveryFields_${selectedCompany._id}`;
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.recoveryFields && Array.isArray(parsed.recoveryFields)) {
          setRecoveryFields(parsed.recoveryFields);
        }
      }
    } catch (error) {
      console.error("Load recovery fields error:", error);
    }
  };

  const fetchRecoveries = async (companyId) => {
    if (!companyId) {
      setRecoveries([]);
      return;
    }

    setIsLoading(true);
    try {
      const token = getToken();
      // Note: This requires backend admin routes for recovery management
      const response = await api.get(`/recovery/admin/company/${companyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(async () => {
        return { data: { recoveries: [] } };
      });

      if (response.data && response.data.recoveries) {
        setRecoveries(response.data.recoveries);
      } else {
        setRecoveries([]);
      }
    } catch (error) {
      console.error("Fetch recoveries error:", error);
      setRecoveries([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCompanySelect = (companyId) => {
    const company = companies.find(c => c._id === companyId);
    setSelectedCompany(company);
    if (companyId) {
      fetchRecoveries(companyId);
    } else {
      setRecoveries([]);
    }
  };


  const handleDelete = async (recoveryId) => {
    if (!window.confirm("Are you sure you want to delete this recovery?")) {
      return;
    }

    try {
      setIsLoading(true);
      const token = getToken();
      await api.delete(`/recovery/admin/${recoveryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => {
        throw new Error("Delete endpoint not available");
      });

      toast.success("Recovery deleted successfully!");
      if (selectedCompany) {
        await fetchRecoveries(selectedCompany._id);
      }
    } catch (error) {
      console.error("Delete recovery error:", error);
      toast.error("Delete functionality requires admin backend routes");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditRecovery = (recovery) => {
    setRecoveryFormData({
      serialNumber: recovery.serialNumber || '',
      customerPartyId: recovery.customerPartyId || '',
      customerName: recovery.customerName || '',
      customerNameUrdu: recovery.customerNameUrdu || '',
      customerVehicleNumber: recovery.customerVehicleNumber || '',
      customerPhone: recovery.customerPhone || '',
      recoveryAmount: recovery.recoveryAmount || '',
      paymentMethod: recovery.paymentMethod || '',
      bankId: recovery.bankId || '',
      description: recovery.description || '',
      descriptionUrdu: recovery.descriptionUrdu || '',
      date: recovery.date ? new Date(recovery.date).toLocaleDateString('en-CA') : new Date().toLocaleDateString('en-CA')
    });
    setEditingRecovery(recovery);
    setShowRecoveryModal(true);
  };

  const handleCreateRecovery = async () => {
    if (!selectedCompany) {
      toast.error("Please select a company first");
      return;
    }

    try {
      setIsLoading(true);
      const token = getToken();

      const recoveryPayload = {
        companyId: selectedCompany._id,
        serialNumber: recoveryFormData.serialNumber,
        customerName: recoveryFormData.customerName,
        customerNameUrdu: recoveryFormData.customerNameUrdu,
        customerVehicleNumber: recoveryFormData.customerVehicleNumber,
        customerPhone: recoveryFormData.customerPhone,
        recoveryAmount: parseFloat(recoveryFormData.recoveryAmount) || 0,
        paymentMethod: recoveryFormData.paymentMethod,
        bankId: recoveryFormData.bankId || '',
        description: recoveryFormData.description,
        descriptionUrdu: recoveryFormData.descriptionUrdu,
        date: recoveryFormData.date
      };

      if (editingRecovery) {
        await api.put(`/recovery/admin/${editingRecovery._id}`, recoveryPayload, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => {
          throw new Error("Update endpoint not available");
        });
        toast.success("Recovery updated successfully!");
      } else {
        await api.post("/recovery/admin/create", recoveryPayload, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => {
          throw new Error("Create endpoint not available");
        });
        toast.success("Recovery created successfully!");
      }

      setShowRecoveryModal(false);
      setEditingRecovery(null);
      setRecoveryFormData({
        serialNumber: '',
        customerPartyId: '',
        customerName: '',
        customerNameUrdu: '',
        customerVehicleNumber: '',
        customerPhone: '',
        recoveryAmount: '',
        paymentMethod: '',
        bankId: '',
        description: '',
        descriptionUrdu: '',
        date: new Date().toLocaleDateString('en-CA')
      });
      await fetchRecoveries(selectedCompany._id);
    } catch (error) {
      console.error("Create/Update recovery error:", error);
      toast.error(error.response?.data?.message || "This requires admin backend routes");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Helmet><title>Recovery Management - Admin Panel</title></Helmet>
      <div className="min-h-screen bg-brand-background p-4 md:p-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Recovery Management</h1>
                <p className="text-gray-600 mt-1">Manage recovery data for companies</p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => navigate("/admin")}
                  className="bg-gray-600 hover:bg-gray-700 text-white font-semibold px-4 py-2 rounded-lg transition"
                >
                  Back to Admin Panel
                </button>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Left Sidebar - Company Selection */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Select Company</h2>
              <select
                onChange={(e) => handleCompanySelect(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600 mb-4"
              >
                <option value="">-- Select Company --</option>
                {companies.map((company) => (
                  <option key={company._id} value={company._id}>
                    {company.shopName || company.username} ({company.email})
                  </option>
                ))}
              </select>

              {selectedCompany && (
                <div className="space-y-4">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h3 className="font-semibold text-gray-900 mb-2">Company Info</h3>
                    <p className="text-sm text-gray-600">Name: {selectedCompany.shopName || selectedCompany.username}</p>
                    <p className="text-sm text-gray-600">Email: {selectedCompany.email}</p>
                  </div>

                  <div className="space-y-2">
                    <button
                      onClick={() => setShowFieldModal(true)}
                      className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-4 py-2 rounded-lg transition"
                    >
                      Manage Form Fields
                    </button>
                    <button
                      onClick={() => {
                        setRecoveryFormData({
                          serialNumber: '',
                          customerPartyId: '',
                          customerName: '',
                          customerNameUrdu: '',
                          customerVehicleNumber: '',
                          customerPhone: '',
                          recoveryAmount: '',
                          paymentMethod: '',
                          bankId: '',
                          description: '',
                          descriptionUrdu: '',
                          date: new Date().toLocaleDateString('en-CA')
                        });
                        setEditingRecovery(null);
                        setShowRecoveryModal(true);
                      }}
                      className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold px-4 py-2 rounded-lg transition"
                    >
                      Create New Recovery
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Right Side - Recoveries List */}
            <div className="md:col-span-2 bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                Recoveries {selectedCompany && `- ${selectedCompany.shopName || selectedCompany.username}`}
              </h2>

              {!selectedCompany ? (
                <div className="text-center py-12 text-gray-500">
                  <p>Please select a company to view recoveries</p>
                </div>
              ) : isLoading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
                </div>
              ) : recoveries.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <p>No recoveries found. Create your first recovery!</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {recoveries.map((recovery) => (
                    <div key={recovery._id || recovery.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-semibold text-gray-900">
                            Recovery #{recovery.serialNumber || recovery._id?.slice(-6)}
                          </h3>
                          <p className="text-sm text-gray-600">
                            Customer: {recovery.customerNameUrdu || recovery.customerName || 'N/A'}
                          </p>
                          <p className="text-sm text-gray-600">
                            Date: {recovery.date ? new Date(recovery.date).toLocaleDateString() : 'N/A'}
                          </p>
                          <p className="text-sm text-gray-600">
                            Vehicle: {recovery.customerVehicleNumber || 'N/A'}
                          </p>
                          <p className="text-sm text-gray-600">
                            Phone: {recovery.customerPhone || 'N/A'}
                          </p>
                          <p className="text-sm font-semibold text-green-600 mt-2">
                            Amount: Rs. {parseFloat(recovery.recoveryAmount || 0).toFixed(2)}
                          </p>
                          <p className="text-sm text-gray-600">
                            Payment: {recovery.paymentMethod || 'N/A'}
                          </p>
                          {recovery.descriptionUrdu || recovery.description ? (
                            <p className="text-sm text-gray-600 mt-1">
                              Description: {recovery.descriptionUrdu || recovery.description}
                            </p>
                          ) : null}
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => window.open(`/recovery/view/${recovery._id || recovery.id}`, '_blank')}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-3 py-1 rounded text-sm"
                          >
                            View
                          </button>
                          <button
                            onClick={() => handleEditRecovery(recovery)}
                            className="bg-yellow-600 hover:bg-yellow-700 text-white font-semibold px-3 py-1 rounded text-sm"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(recovery._id || recovery.id)}
                            className="bg-red-600 hover:bg-red-700 text-white font-semibold px-3 py-1 rounded text-sm"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Create/Edit Recovery Modal */}
        {showRecoveryModal && selectedCompany && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  {editingRecovery ? 'Edit Recovery' : 'Create New Recovery'}
                </h2>
                <button
                  onClick={() => {
                    setShowRecoveryModal(false);
                    setEditingRecovery(null);
                    setRecoveryFormData({
                      serialNumber: '',
                      customerPartyId: '',
                      customerName: '',
                      customerNameUrdu: '',
                      customerVehicleNumber: '',
                      customerPhone: '',
                      recoveryAmount: '',
                      paymentMethod: '',
                      bankId: '',
                      description: '',
                      descriptionUrdu: '',
                      date: new Date().toLocaleDateString('en-CA')
                    });
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Serial Number</label>
                    <input
                      type="text"
                      value={recoveryFormData.serialNumber}
                      onChange={(e) => setRecoveryFormData({ ...recoveryFormData, serialNumber: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      placeholder="Auto-generated if empty"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
                    <input
                      type="date"
                      value={recoveryFormData.date}
                      onChange={(e) => setRecoveryFormData({ ...recoveryFormData, date: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Customer</label>
                    <select
                      value={recoveryFormData.customerPartyId}
                      onChange={(e) => {
                        const party = ledgerParties.find(p => p._id === e.target.value);
                        setRecoveryFormData({
                          ...recoveryFormData,
                          customerPartyId: e.target.value,
                          customerName: party?.name || '',
                          customerNameUrdu: party?.nameUrdu || party?.name || '',
                          customerPhone: party?.phone || ''
                        });
                      }}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="">-- Select Customer --</option>
                      {ledgerParties.filter(p => p.type === 'customer').map(party => (
                        <option key={party._id} value={party._id}>
                          {party.nameUrdu || party.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Customer Name (English)</label>
                    <input
                      type="text"
                      value={recoveryFormData.customerName}
                      onChange={(e) => setRecoveryFormData({ ...recoveryFormData, customerName: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      placeholder="Customer Name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Customer Name (Urdu)</label>
                    <input
                      type="text"
                      value={recoveryFormData.customerNameUrdu}
                      onChange={(e) => setRecoveryFormData({ ...recoveryFormData, customerNameUrdu: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      placeholder="کسٹمر کا نام"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Vehicle Number</label>
                    <input
                      type="text"
                      value={recoveryFormData.customerVehicleNumber}
                      onChange={(e) => setRecoveryFormData({ ...recoveryFormData, customerVehicleNumber: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      placeholder="Vehicle Number"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                    <input
                      type="text"
                      value={recoveryFormData.customerPhone}
                      onChange={(e) => setRecoveryFormData({ ...recoveryFormData, customerPhone: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      placeholder="Phone Number"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Recovery Amount</label>
                    <input
                      type="number"
                      step="0.01"
                      value={recoveryFormData.recoveryAmount}
                      onChange={(e) => setRecoveryFormData({ ...recoveryFormData, recoveryAmount: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      placeholder="0.00"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Payment Method</label>
                    <select
                      value={recoveryFormData.paymentMethod}
                      onChange={(e) => {
                        const pm = paymentMethods.find(p => (p.nameUrdu || p.name) === e.target.value);
                        setRecoveryFormData({
                          ...recoveryFormData,
                          paymentMethod: e.target.value,
                          bankId: pm?._id || ''
                        });
                      }}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="">-- Select Payment Method --</option>
                      {paymentMethods.map(pm => (
                        <option key={pm._id} value={pm.nameUrdu || pm.name}>
                          {pm.nameUrdu || pm.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Description (English)</label>
                    <textarea
                      value={recoveryFormData.description}
                      onChange={(e) => setRecoveryFormData({ ...recoveryFormData, description: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      rows="2"
                      placeholder="Description"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Description (Urdu)</label>
                    <textarea
                      value={recoveryFormData.descriptionUrdu}
                      onChange={(e) => setRecoveryFormData({ ...recoveryFormData, descriptionUrdu: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      rows="2"
                      placeholder="تفصیل"
                    />
                  </div>
                </div>

                <div className="flex gap-4 pt-4">
                  <button
                    onClick={() => {
                      setShowRecoveryModal(false);
                      setEditingRecovery(null);
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateRecovery}
                    disabled={isLoading}
                    className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg disabled:opacity-50"
                  >
                    {isLoading ? "Saving..." : editingRecovery ? "Update Recovery" : "Create Recovery"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Field Management Modal */}
        {showFieldModal && selectedCompany && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <h2 className="text-2xl font-bold text-gray-900">Manage Recovery Form Fields</h2>
                  <button
                    onClick={() => setShowFieldModal(false)}
                    className="text-gray-500 hover:text-gray-700 text-2xl"
                  >
                    ×
                  </button>
                </div>
                <p className="text-gray-600 mt-2">Add, edit, or remove fields for the recovery form</p>
              </div>

              <div className="p-6">
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-lg font-semibold text-gray-900">Recovery Form Fields</h3>
                    <button
                      onClick={() => {
                        setRecoveryFields([...recoveryFields, {
                          fieldName: '',
                          fieldNameUrdu: '',
                          fieldType: 'text',
                          isRequired: false,
                          order: recoveryFields.length,
                          isDefault: false
                        }]);
                      }}
                      className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-semibold"
                    >
                      + Add Field
                    </button>
                  </div>

                  <div className="space-y-3">
                    {recoveryFields.map((field, index) => (
                      <div key={index} className="border border-gray-300 rounded-lg p-4 bg-gray-50">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Field Name (English)</label>
                            <ReactQuill
                              theme="snow"
                              value={field.fieldName || ''}
                              onChange={(value) => {
                                const updated = [...recoveryFields];
                                updated[index] = { ...updated[index], fieldName: value };
                                setRecoveryFields(updated);
                              }}
                              placeholder="Field Name"
                              modules={quillModules}
                              style={{ minHeight: '100px', marginBottom: '40px' }}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Field Name (Urdu)</label>
                            <ReactQuill
                              theme="snow"
                              value={field.fieldNameUrdu || ''}
                              onChange={(value) => {
                                const updated = [...recoveryFields];
                                updated[index] = { ...updated[index], fieldNameUrdu: value };
                                setRecoveryFields(updated);
                              }}
                              placeholder="فیلڈ کا نام"
                              modules={quillModules}
                              style={{ minHeight: '100px', marginBottom: '40px' }}
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Field Type</label>
                            <select
                              value={field.fieldType}
                              onChange={(e) => {
                                const updated = [...recoveryFields];
                                updated[index] = { ...updated[index], fieldType: e.target.value };
                                setRecoveryFields(updated);
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                            >
                              <option value="text">Text</option>
                              <option value="number">Number</option>
                              <option value="date">Date</option>
                              <option value="textarea">Textarea</option>
                            </select>
                          </div>
                          <div className="flex items-center">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={field.isRequired || false}
                                onChange={(e) => {
                                  const updated = [...recoveryFields];
                                  updated[index] = { ...updated[index], isRequired: e.target.checked };
                                  setRecoveryFields(updated);
                                }}
                                className="w-4 h-4"
                              />
                              <span className="text-sm text-gray-700">Required</span>
                            </label>
                          </div>
                          <div className="flex justify-end gap-2">
                            {!field.isDefault && (
                              <button
                                onClick={() => {
                                  const updated = recoveryFields.filter((_, i) => i !== index);
                                  setRecoveryFields(updated);
                                }}
                                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-semibold"
                              >
                                Delete
                              </button>
                            )}
                            {field.isDefault && (
                              <span className="text-xs text-gray-500 px-3 py-2">Default Field</span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={() => {
                    // Reset to default fields
                    setRecoveryFields([
                      { fieldName: 'Date', fieldNameUrdu: 'تاریخ', fieldType: 'date', isRequired: true, order: 0, isDefault: true },
                      { fieldName: 'Customer Name', fieldNameUrdu: 'کسٹمر کا نام', fieldType: 'text', isRequired: true, order: 1, isDefault: true },
                      { fieldName: 'Customer Name Urdu', fieldNameUrdu: 'کسٹمر کا نام اردو', fieldType: 'text', isRequired: false, order: 2, isDefault: true },
                      { fieldName: 'Vehicle Number', fieldNameUrdu: 'گاڑی نمبر', fieldType: 'text', isRequired: false, order: 3, isDefault: true },
                      { fieldName: 'Phone Number', fieldNameUrdu: 'فون نمبر', fieldType: 'text', isRequired: false, order: 4, isDefault: true },
                      { fieldName: 'Recovery Amount', fieldNameUrdu: 'ریکوری کی رقم', fieldType: 'number', isRequired: true, order: 5, isDefault: true },
                      { fieldName: 'Payment Method', fieldNameUrdu: 'ادائیگی کا طریقہ', fieldType: 'text', isRequired: false, order: 6, isDefault: true },
                      { fieldName: 'Description', fieldNameUrdu: 'تفصیل', fieldType: 'textarea', isRequired: false, order: 7, isDefault: true },
                      { fieldName: 'Description Urdu', fieldNameUrdu: 'تفصیل اردو', fieldType: 'textarea', isRequired: false, order: 8, isDefault: true }
                    ]);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Reset to Default
                </button>
                <button
                  onClick={() => setShowFieldModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (!selectedCompany) {
                      toast.error("Please select a company first");
                      return;
                    }

                    try {
                      setIsLoading(true);
                      const token = getToken();

                      // Save to localStorage
                      const storageKey = `recoveryFields_${selectedCompany._id}`;
                      localStorage.setItem(storageKey, JSON.stringify({ recoveryFields }));

                      // Save to backend (when route is ready)
                      // await api.post("/recovery/admin/fields", {
                      //   companyId: selectedCompany._id,
                      //   recoveryFields
                      // }, {
                      //   headers: { Authorization: `Bearer ${token}` }
                      // });

                      toast.success("Recovery fields saved successfully!");
                      setShowFieldModal(false);
                    } catch (error) {
                      console.error("Save recovery fields error:", error);
                      toast.error(error.response?.data?.message || "Failed to save fields");
                    } finally {
                      setIsLoading(false);
                    }
                  }}
                  disabled={isLoading}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg disabled:opacity-50"
                >
                  {isLoading ? "Saving..." : "Save Fields"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default AdminRecoveryManagement;

